import React, { FC } from 'react'
import { Switch, CircularProgress, Box, Stack } from '@mui/material'
import {
  useSettingQuery,
  useUpdateSettingMutation,
  useUpdateUIConfigMutation,
} from '@/features/auth'
import { FontSize, Setting } from '@/features/auth/types/setting'
import { settingDescriptions } from '@/apps/latest/constants/settingDescriptions'
import {
  NotionCheckbox,
  NotionSelect,
  PageScaffold,
  SettingRow,
  SubsectionHeader,
} from '@/apps/latest/components/portal/notion'

/**
 * 表示設定 (UI config) ページ。
 * 端末を跨いで追従させる per-user 表示嗜好を1画面に集約する (= 旧 SOAP基本設定 から
 * 表示系を分離)。 文字サイズ / 改善提案の初期開閉 / 薬歴表示日数 は ui_config、
 * SOAPデフォルト選択は YakurekiUserSetting (flat) を読み書きする。
 */
export const DisplaySettingsPage: FC = () => {
  const { data: customSetting } = useSettingQuery()
  const updateSettingMutation = useUpdateSettingMutation()
  const updateUIConfigMutation = useUpdateUIConfigMutation()

  const updateSetting = (key: keyof Setting, value: Setting[keyof Setting]) => {
    updateSettingMutation.mutate({ key, value })
  }

  if (!customSetting) {
    return (
      <PageScaffold title="表示設定">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageScaffold>
    )
  }

  const ui = customSetting.ui_config
  const fontSize: FontSize = ui?.font_size ?? 'default'
  const improvementsOpen = ui?.improvements_expanded ?? false
  const displayDays = ui?.display_days ?? 7
  const defaults = settingDescriptions.soapDefaultSelection.items
  const display = settingDescriptions.displaySettings.items

  return (
    <PageScaffold title="表示設定">
      <Stack gap={4}>
        <Box>
          <SubsectionHeader heading="画面表示" />
          <SettingRow
            label="文字サイズ"
            description="録音・SOAP画面と患者リストの文字を拡大します。大では患者リストの幅も広がります。"
            control={
              <NotionSelect<FontSize>
                value={fontSize}
                onChange={(v) =>
                  updateUIConfigMutation.mutate({
                    key: 'font_size',
                    value: v,
                  })
                }
                minWidth={120}
                options={[
                  { value: 'default', label: '小' },
                  { value: 'large', label: '中' },
                  { value: 'xlarge', label: '大' },
                ]}
              />
            }
          />
          <SettingRow
            label="改善提案を最初から開く"
            description="SOAP の下の「AI で改善」を、押していない提案があれば開いた状態で始めます。"
            control={
              <Switch
                color="primary"
                checked={improvementsOpen}
                onChange={() =>
                  updateUIConfigMutation.mutate({
                    key: 'improvements_expanded',
                    value: !improvementsOpen,
                  })
                }
              />
            }
          />
          <SettingRow
            label={display.display_days.title}
            description={display.display_days.description}
            control={
              <NotionSelect<number>
                value={displayDays}
                onChange={(v) =>
                  updateUIConfigMutation.mutate({
                    key: 'display_days',
                    value: v,
                  })
                }
                minWidth={120}
                options={[1, 2, 3, 4, 5, 6, 7].map((d) => ({
                  value: d,
                  label: `${d}日`,
                }))}
              />
            }
          />
        </Box>

        <Box>
          <SubsectionHeader
            heading={settingDescriptions.soapDefaultSelection.title}
            description="生成時にチェックを入れておく項目。"
          />
          <Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={0.5}>
            {Object.entries(defaults).map(([key, item]) => (
              <NotionCheckbox
                key={key}
                label={item.title.split('（')[0]}
                checked={
                  (customSetting[key as keyof Setting] as boolean) ?? true
                }
                onChange={() =>
                  updateSetting(
                    key as keyof Setting,
                    !((customSetting[key as keyof Setting] as boolean) ?? true)
                  )
                }
              />
            ))}
          </Stack>
        </Box>
      </Stack>
    </PageScaffold>
  )
}

export default DisplaySettingsPage
